import {
  getPropertyTypeDef,
  getListingTypesFor,
  validateListingValues,
  listingToValues,
} from "../../shared/listingRules";

// The web wizard's steps and the fields each one owns, so "Next" only
// complains about what is on screen. Field keys match validateListingValues.
export const LISTING_STEPS = [
  { id: "property", label: "Property", fields: ["listingType", "propertyCategory", "propertyType"] },
  { id: "location", label: "Location", fields: ["locality", "city", "pincode"] },
  { id: "details", label: "Details", fields: ["rooms", "areaValue", "pgFor", "floorNumber"] },
  { id: "pricing", label: "Pricing", fields: ["price", "pgSharing"] },
  { id: "publish", label: "Publish", fields: ["title", "description"] },
];

/** A blank listing: no pre-filled bedrooms, bathrooms or area. */
export function createEmptyValues() {
  // listingToValues of an empty doc gives every optional field its empty
  // shape; the transaction and type start unchosen.
  return {
    ...listingToValues({}),
    listingType: "",
    propertyType: "",
    propertyCategory: "residential",
    possession: "ready",
    readyToMove: true,
    availableFrom: "Immediately",
  };
}

/**
 * Values for editing a stored listing: legacy 'apartment' / 'sell' /
 * commercial 'rent' / text prices are normalised, and types without
 * bedrooms never get the old "2 beds, 1 bath" defaults.
 */
export function valuesFromListing(listing) {
  return listingToValues(listing);
}

/** 'sell' | 'rent' | 'pg' | '': the first choice the lister makes. */
export function getIntent(values) {
  if (values.propertyType === "pg") return "pg";
  if (values.listingType === "buy") return "sell";
  if (values.listingType === "rent" || values.listingType === "lease") return "rent";
  return "";
}

function rentFor(category) {
  return category === "commercial" ? "lease" : "rent";
}

function keepTypeIfAllowed(values, category, listingType) {
  const def = getPropertyTypeDef(category, values.propertyType);
  const allowed = def
    && def.category === category
    && def.id !== "pg"
    && getListingTypesFor(category, def.id).includes(listingType);
  return allowed ? values.propertyType : "";
}

export function applyIntent(values, intent) {
  if (intent === "pg") {
    return { ...values, propertyCategory: "residential", listingType: "rent", propertyType: "pg" };
  }
  const category = values.propertyCategory || "residential";
  const listingType = intent === "sell" ? "buy" : rentFor(category);
  return { ...values, listingType, propertyType: keepTypeIfAllowed(values, category, listingType) };
}

export function applyCategory(values, category) {
  const intent = getIntent(values);
  const listingType = intent === "sell" ? "buy" : intent === "rent" ? rentFor(category) : values.listingType;
  return {
    ...values,
    propertyCategory: category,
    listingType,
    propertyType: keepTypeIfAllowed(values, category, listingType),
  };
}

/** Errors for the fields shown on one step. */
export function getStepErrors(stepIndex, values) {
  const all = validateListingValues(values);
  const step = LISTING_STEPS[stepIndex];
  if (!step) return {};
  return step.fields.reduce((acc, key) => {
    if (all[key]) acc[key] = all[key];
    return acc;
  }, {});
}

/** Index of the first step with a problem, or -1 when the listing is valid. */
export function getFirstInvalidStep(values) {
  for (let i = 0; i < LISTING_STEPS.length; i += 1) {
    if (Object.keys(getStepErrors(i, values)).length > 0) return i;
  }
  return -1;
}

export const LOCATION_STEP = LISTING_STEPS.findIndex((step) => step.id === "location");

/**
 * Whether a listing has a real map pin. The listing forms' maps open on a
 * default point, and saving it untouched put listings in central Bengaluru
 * (wrong map, wrong nearby search, nearby leads shared with the wrong
 * dealers). (0, 0) is how older listings recorded "no location".
 */
export function hasPinnedCoordinates(latitude, longitude) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  return Number.isFinite(lat) && Number.isFinite(lng)
    && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
    && !(lat === 0 && lng === 0);
}
